import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { SilentRequest } from '@azure/msal-browser';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ImpersonationService } from 'src/app/services/impersonation/impersonation.service';
import { ErrorReportingService } from 'src/app/services/error-reporting/error-reporting.service';
import { UnexpectedErrorDialogService } from 'src/app/services/error-reporting/unexpected-error-dialog.service';
import { TOLERATE_FAILURE } from 'src/app/interceptors/tolerate-failure.token';
import { EXPECTED_STATUSES } from 'src/app/interceptors/expected-statuses.token';

const API_SCOPES = [
  'api://095477a9-2dfb-478b-8c6e-90be6704d6ad/API.Read',
  'api://095477a9-2dfb-478b-8c6e-90be6704d6ad/API.Write',
];

/** How long an API request may wait for a token refresh before it fails. */
export const TOKEN_WAIT_TIMEOUT_MS = 30_000;

@Injectable()
export class CalystaAPIInterceptor implements HttpInterceptor {
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private refreshing = false;

  constructor(
    private msal: MsalService,
    private impersonation: ImpersonationService,
    private errorReporting: ErrorReportingService,
    private errorDialog: UnexpectedErrorDialogService,
  ) {
    // Local dev bypass: no Azure AD, so never touch MSAL (it would force a login).
    if (environment.authBypass) return;
    this.acquireToken();
    setInterval(() => this.proactiveRenewal(), 300_000);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Dev bypass: pass requests through with no bearer; the API's dev scheme authenticates.
    if (environment.authBypass) {
      return next.handle(req).pipe(catchError(err => this.reportAndRethrow(err, req)));
    }

    if (!req.urlWithParams.startsWith(environment.CalystaApiBaseURL)) {
      return next.handle(req).pipe(catchError(err => this.reportAndRethrow(err, req)));
    }

    return this.waitForToken().pipe(
      switchMap(token => next.handle(withBearer(req, token, this.impersonation.header))),
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          return this.handle401(req, next);
        }
        return this.reportAndRethrow(err, req);
      })
    );
  }

  /**
   * Classifies err as expected/unexpected, captures and tags the unexpected case, shows the
   * persistent dialog for it, then rethrows unconditionally either way -- callers' existing
   * subscribe error handlers (loading spinners, button states) must keep firing exactly as
   * before. notify-http-error.ts checks the same tag and skips its own toast for the unexpected
   * case, so the user sees the persistent dialog once, not the dialog and a toast together.
   * <para>
   * A request tagged TOLERATE_FAILURE (a bounded retry loop, a best-effort refresh) skips both
   * capture and dialog entirely -- the caller already designed around this failure, so treating
   * it as "unexpected" would misinform the user about an operation that isn't actually broken.
   * </para>
   * <para>
   * A request tagged EXPECTED_STATUSES additionally skips capture and dialog when the error is an
   * HttpErrorResponse whose status is in that (status-scoped) list -- e.g. a claim/release race
   * losing to 403/404/409/410 is an expected domain outcome, not a defect, but a genuine 500 on
   * the very same request must still alert. 401 is never suppressed this way: the token-refresh
   * flow above already intercepts 401 before it reaches here, and this check ignores it as a
   * defence in depth in case that ever changes.
   * </para>
   */
  private reportAndRethrow(err: unknown, req: HttpRequest<any>): Observable<never> {
    const isExpectedStatus =
      err instanceof HttpErrorResponse &&
      err.status !== 401 &&
      req.context.get(EXPECTED_STATUSES).includes(err.status);

    if (!req.context.get(TOLERATE_FAILURE) && !isExpectedStatus) {
      const unexpected = this.errorReporting.reportIfUnexpected(err);
      if (unexpected) {
        this.errorDialog.show(unexpected.reference);
      }
    }
    return throwError(() => err);
  }

  // Waits until a valid token is available. If a refresh is in progress,
  // queues behind it rather than flying out with the stale cached value.
  private waitForToken(): Observable<string> {
    const current = this.tokenSubject.getValue();
    if (current) return of(current);
    return this.nextToken();
  }

  // The next token from a refresh. Bounded: if the refresh fails and the login redirect never happens,
  // an unbounded wait leaves every API call pending forever with no error (an AI time-entry Confirm
  // that silently saved nothing). The request has not been sent yet, so failing it is safe to retry.
  private nextToken(): Observable<string> {
    return this.tokenSubject.pipe(
      filter((t): t is string => t !== null),
      take(1),
      timeout({
        first: TOKEN_WAIT_TIMEOUT_MS,
        with: () => throwError(() => new Error('Timed out waiting for an access token; the request was not sent.')),
      })
    );
  }

  private handle401(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.refreshing) {
      // Another request already triggered a refresh — wait for it.
      return this.nextToken().pipe(
        switchMap(token => next.handle(withBearer(req, token, this.impersonation.header)))
      );
    }

    this.refreshing = true;
    this.tokenSubject.next(null); // gate concurrent requests behind the refresh

    return this.msal.acquireTokenSilent({
      scopes: API_SCOPES,
      account: this.msal.instance.getActiveAccount(),
      forceRefresh: true, // bypass cache — the cached token was just rejected
    }).pipe(
      switchMap(result => {
        this.refreshing = false;
        this.tokenSubject.next(result.accessToken);
        return next.handle(withBearer(req, result.accessToken, this.impersonation.header));
      }),
      catchError(err => {
        this.refreshing = false;
        this.tokenSubject.next(null);
        this.msal.loginRedirect().subscribe();
        return throwError(() => err);
      })
    );
  }

  private acquireToken(): void {
    const account =
      this.msal.instance.getActiveAccount() ??
      this.msal.instance.getAllAccounts()[0];

    if (!account) {
      this.msal.loginPopup().subscribe(r => {
        this.msal.instance.setActiveAccount(r.account);
        this.acquireToken();
      });
      return;
    }

    this.msal.instance.setActiveAccount(account);
    this.msal.acquireTokenSilent({ scopes: API_SCOPES, account }).subscribe({
      next: r => this.tokenSubject.next(r.accessToken),
      error: () => this.msal.loginRedirect().subscribe(),
    });
  }

  // First line of defence: renew the token before it expires so most requests
  // never see a 401 in the first place. The 401 handler is the safety net.
  private proactiveRenewal(): void {
    const exp = this.msal.instance.getActiveAccount()?.idTokenClaims?.exp;
    if (!exp || exp - Date.now() / 1000 >= 300) return;
    this.tokenSubject.next(null);
    this.acquireToken();
  }
}

function withBearer(
  req: HttpRequest<any>,
  token: string,
  impersonateEmail: string | null,
): HttpRequest<any> {
  const setHeaders: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (impersonateEmail) setHeaders['X-Impersonate-Email'] = impersonateEmail;
  return req.clone({ setHeaders });
}
